import { Link } from 'react-router-dom';
import { SITE_INFO, FALLBACK_SERVICES } from '../data';

function InstagramIcon({ className = "w-4 h-4", ...props }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  );
}

export default function Footer() {
  return (
    <>
      {/* Black and White Map Section above Footer */}
      <div className="relative w-full h-[320px] sm:h-[380px] bg-stone-100 overflow-hidden border-t border-stone-200">
        <iframe
          title="G28 ApS Beliggenhed"
          src="https://www.google.com/maps?q=Ole+R%C3%B8mers+Vej+32,+8670+L%C3%A5sby&amp;output=embed"
          className="w-full h-full grayscale contrast-[110%] brightness-[102%] border-0 opacity-95 transition-opacity duration-300 hover:opacity-100"
          loading="lazy"
        ></iframe>

        {/* Floating Info Overlay Card */}
      </div>

      <footer className="bg-stone-950 text-stone-400 text-sm py-12 border-t border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 pb-10 border-b border-stone-800/80">
            
            {/* Brand */}
            <div className="md:col-span-4 space-y-3">
              <Link to="/" className="inline-block transition-transform duration-300 hover:scale-105 origin-left">
                <img
                  src="https://assets.auxodev.dk/assets/58b18d64-1cfa-413a-b5ba-90838c1f6d2c/g28-nyt-logo-rez.png"
                  alt="G28 Entreprise | Tømrer | Murer"
                  className="h-16 sm:h-20 w-auto object-contain brightness-0 invert"
                />
              </Link>
            </div>

            {/* Navigation */}
            <div className="md:col-span-2">
              <h3 className="font-heading font-semibold text-white text-xs uppercase tracking-wider mb-3">
                Navigation
              </h3>
              <ul className="space-y-2 text-xs sm:text-sm">
                <li><Link to="/" className="hover:text-white transition-colors">Forside</Link></li>
                <li><Link to="/ydelser/tagarbejde" className="hover:text-white transition-colors">Ydelser</Link></li>
                <li><Link to="/projektudvikling" className="hover:text-white transition-colors">Projektudvikling</Link></li>
                <li><Link to="/projekter" className="hover:text-white transition-colors">Projekter</Link></li>
                <li><Link to="/galleri" className="hover:text-white transition-colors">Galleri</Link></li>
                <li><Link to="/om-os" className="hover:text-white transition-colors">Om os</Link></li>
                <li><Link to="/ledige-stillinger" className="hover:text-white transition-colors">Ledige stillinger</Link></li>
                <li><Link to="/kontakt" className="hover:text-white transition-colors">Kontakt</Link></li>
              </ul>
            </div>

            {/* Core Services */}
            <div className="md:col-span-3">
              <h3 className="font-heading font-semibold text-white text-xs uppercase tracking-wider mb-3">
                Ydelser
              </h3>
              <ul className="space-y-2 text-xs sm:text-sm">
                {FALLBACK_SERVICES.map((srv) => (
                  <li key={srv.url_slug}>
                    <Link to={`/ydelser/${srv.url_slug}`} className="hover:text-white transition-colors">
                      {srv.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Direct Contact */}
            <div className="md:col-span-3 space-y-2 text-xs sm:text-sm">
              <h3 className="font-heading font-semibold text-white text-xs uppercase tracking-wider mb-3">
                Kontakt
              </h3>
              <p>
                <a href={`tel:${SITE_INFO.phone.replace(/\s+/g, '')}`} className="font-medium text-white hover:text-amber-400 transition-colors whitespace-nowrap">
                  {SITE_INFO.phone}
                </a>
              </p>
              <p>
                <a href={`mailto:${SITE_INFO.email}`} className="hover:text-white transition-colors">
                  {SITE_INFO.email}
                </a>
              </p>
              <p className="text-stone-400">{SITE_INFO.address}</p>
              <p className="text-stone-500 pt-1">CVR: {SITE_INFO.cvr}</p>
              {SITE_INFO.instagram && (
                <div className="pt-2">
                  <a
                    href={SITE_INFO.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-stone-300 hover:text-amber-400 transition-colors group"
                  >
                    <span className="w-7 h-7 rounded bg-stone-900 border border-stone-800 flex items-center justify-center group-hover:border-amber-500/50 group-hover:text-amber-400 text-stone-300 transition-all">
                      <InstagramIcon className="w-4 h-4" aria-hidden="true" />
                    </span>
                    <span className="font-medium text-xs">Følg os på Instagram</span>
                  </a>
                </div>
              )}
            </div>

          </div>

          {/* Bottom Bar */}
          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-stone-500 gap-4">
            <p>© {new Date().getFullYear()} {SITE_INFO.name}. Alle rettigheder forbeholdes.</p>
            <div className="flex items-center gap-4">
              <a
                href={SITE_INFO.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-amber-400 transition-colors flex items-center gap-1.5"
                aria-label="G28 på Instagram"
              >
                <InstagramIcon className="w-3.5 h-3.5" aria-hidden="true" />
                <span>{SITE_INFO.instagramHandle}</span>
              </a>
              <span className="text-stone-700">•</span>
              <span>Faglært Tømrerarbejde</span>
            </div>
          </div>

        </div>
      </footer>
    </>
  );
}
